import { useMemo } from "react";
import dayjs from "dayjs";
import { parseDataKey } from "../utils/dateRange";
import { aggregateMinutes } from "../utils/aggregateMinutes";

/* =========================
   HELPERS
========================= */

function uiDateToDataDay(uiDate) {
  if (!uiDate) return null;
  const [y, m, d] = uiDate.split("-");
  const dt = dayjs(`${d}-${m}-${y}`, "DD-MM-YYYY", true);
  return dt.isValid() ? dt : null;
}

function getWeekStartMonday(d) {
  const offset = (d.day() + 6) % 7; // Mon=0 ... Sun=6
  return d.subtract(offset, "day").startOf("day");
}

// 🔑 NEW — time → minute index
// function timeToMinuteIndex(t) {
//   if (!t) return null;
//   const [hh, mm] = t.split(":").map(Number);
//   if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
//   return hh * 60 + mm;
// }

/* =========================
   MAIN HOOK
========================= */

export function useSwitchInSeries({
  rawImpressions,
  sortedImpKeys,
  startDateInput,
  endDateInput,
  startTime,
  endTime,
  mode,
}) {
  return useMemo(() => {
    if (!rawImpressions || !sortedImpKeys?.length) return [];

    /* =========================
       MINUTE MODE (FIXED)
       ✔ respects time selector
       ✔ keeps full 1440 axis
    ========================= */
    if (mode === "minute") {
      const uiKey = startDateInput
        ? startDateInput.split("-").reverse().join("-")
        : sortedImpKeys.at(-1);

      const arr = Array.isArray(rawImpressions[uiKey])
        ? rawImpressions[uiKey]
        : [];

      const toMinute = (t) => {
        const [h, m] = t.split(":").map(Number);
        return h * 60 + m;
      };

      const startMinute = toMinute(startTime);
      const endMinute = toMinute(endTime);
      if (endMinute < startMinute) return [];

      const out = [];
      for (let i = startMinute; i <= endMinute; i++) {
        const hh = String(Math.floor(i / 60)).padStart(2, "0");
        const mm = String(i % 60).padStart(2, "0");
        out.push({
          index: i,                 // minute of the day — the chart's clock axis
          label: `${hh}:${mm}`,     // 🔑 CATEGORY
          value: Number(arr[i]) || 0,
        });
      }

      return out;
    }


    /* =========================
       DATE RANGE (OTHER MODES)
    ========================= */

    const start = uiDateToDataDay(startDateInput);
    const end = uiDateToDataDay(endDateInput);
    if (!start || !end) return [];

    /* =========================
       HOUR MODE
    ========================= */
    if (mode === "hour") {
      const out = [];

      sortedImpKeys.forEach((key) => {
        const d = parseDataKey(key);
        if (!d) return;
        if (d.isBefore(start, "day") || d.isAfter(end, "day")) return;

        const arr = rawImpressions[key] || [];
        const hours = aggregateMinutes(arr, "hour");

        hours.forEach((v, h) => {
          out.push({
            label: `${d.format("DD.MM")} ${String(h).padStart(2, "0")}:00`,
            value: Number(v) || 0,
          });
        });
      });

      return out;
    }

    /* =========================
       DAY MODE
    ========================= */
    if (mode === "day") {
      return sortedImpKeys
        .map((key) => {
          const d = parseDataKey(key);
          if (!d) return null;
          if (d.isBefore(start, "day") || d.isAfter(end, "day")) return null;

          const arr = rawImpressions[key] || [];
          const [value] = aggregateMinutes(arr, "day");

          return {
            label: d.format("DD.MM"),
            value: Number(value) || 0,
          };
        })
        .filter(Boolean);
    }

    /* =========================
       WEEK MODE
    ========================= */
    if (mode === "week") {
      const bucket = new Map();

      sortedImpKeys.forEach((key) => {
        const d = parseDataKey(key);
        if (!d) return;
        if (d.isBefore(start, "day") || d.isAfter(end, "day")) return;

        const weekStart = getWeekStartMonday(d);
        const bucketKey = weekStart.format("YYYY-MM-DD");

        const arr = rawImpressions[key] || [];
        const [value] = aggregateMinutes(arr, "day");

        bucket.set(
          bucketKey,
          (bucket.get(bucketKey) || 0) + (Number(value) || 0)
        );
      });

      return Array.from(bucket.entries())
        .sort((a, b) => (a[0] < b[0] ? -1 : 1))
        .map(([key, value]) => ({
          label: dayjs(key, "YYYY-MM-DD").format("DD.MM"),
          value,
        }));
    }

    /* =========================
       MONTH MODE
    ========================= */
    if (mode === "month") {
      const bucket = new Map();

      sortedImpKeys.forEach((key) => {
        const d = parseDataKey(key);
        if (!d) return;
        if (d.isBefore(start, "day") || d.isAfter(end, "day")) return;

        const bucketKey = d.format("YYYY-MM");

        const arr = rawImpressions[key] || [];
        const [value] = aggregateMinutes(arr, "day");

        bucket.set(
          bucketKey,
          (bucket.get(bucketKey) || 0) + (Number(value) || 0)
        );
      });

      return Array.from(bucket.entries())
        .sort((a, b) => (a[0] < b[0] ? -1 : 1))
        .map(([key, value]) => ({
          label: dayjs(key, "YYYY-MM").format("MM.YYYY"),
          value,
        }));
    }

    return [];
  }, [
    rawImpressions,
    sortedImpKeys,
    startDateInput,
    endDateInput,
    startTime,
    endTime,
    mode,
  ]);
}
